import React, { useContext, useState } from 'react';
import { EyeOutlined } from '@ant-design/icons';
import { Button, Modal } from 'antd';

import DevicesContainer, {
  containerConfiguration as devicesContainerConfiguration,
} from '@totem/components/devices/devicesContainer/DevicesContainer';
import BuildingMoveDialog from '@totem/components/orgManagement/uiComponents/buildingMoveDialog/buildingMoveDialog';
import OpenBuildingSalesOrderLines from '@totem/components/salesOrders/building/OpenBuildingSalesOrderLines';
import AuvikMappingContainer from '@totem/components/settings/sections/auvikMapping/auvikMappingContainer';
import AuvikMappingTable from '@totem/components/settings/sections/auvikMapping/AuvikMappingTable';
import NinjaOneMappingContainer from '@totem/components/settings/sections/ninjaOneMapping/NinjaOneMappingContainer';
import NinjaOneMappingTable from '@totem/components/settings/sections/ninjaOneMapping/NinjaOneMappingTable';
import BuildingMappingContainer from '@totem/components/settings/sections/nozomi/buildingMapping/BuildingMappingContainer';
import NinjaOneDeviceTable from '@totem/components/ticketing/additional/buildingDecommissioning/ninjaOne/NinjaOneTable';
import NozomiBuildingMappingTable from '@totem/components/ticketing/additional/buildingDecommissioning/nozomi/NozomiBuildingMappingTable';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { Task } from '@totem/components/ticketing/ticketing';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import InputContainer from '@totem/components/ui_components/reportSettings/InputContainer';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { isNotNull } from '@totem/utilities/common';

type Props = {
  task: Task;
};

const TaskSpecificButton = ({ task }: Props) => {
  const { userProfile } = useContext(UserProfileContext);
  const { ticket } = useContext(TicketContext);
  const [showModal, setShowModal] = useState(false);

  if (isNotNull(task) && isNotNull(task.labels)) {
    if (task.labels.includes('CancelContractLines')) {
      return (
        <>
          <Button
            shape="square"
            onClick={() => setShowModal(true)}
            disabled={showModal}
            icon={<EyeOutlined />}
          />
          <Modal
            open={showModal}
            width={'50%'}
            title={`Open Sales Order Lines: ${ticket.building.name}`}
            onCancel={() => {
              setShowModal(false);
            }}
            footer={[
              <Button
                key="cancel"
                onClick={() => {
                  setShowModal(false);
                }}
              >
                Cancel
              </Button>,
            ]}
          >
            <OpenBuildingSalesOrderLines buildingId={ticket.building.id} />
          </Modal>
        </>
      );
    }

    if (task.labels.includes('RemoveDeviceFromNinjaOne')) {
      return (
        <>
          <Button
            shape="square"
            onClick={() => setShowModal(true)}
            disabled={showModal}
            icon={<EyeOutlined />}
          />
          <Modal
            open={showModal}
            width={'50%'}
            title={`NinjaOne Devices: ${ticket.building.name}`}
            onCancel={() => {
              setShowModal(false);
            }}
            footer={[
              <Button
                key="cancel"
                onClick={() => {
                  setShowModal(false);
                }}
              >
                Cancel
              </Button>,
            ]}
          >
            <InputContainer
              filterConfig={devicesContainerConfiguration}
              defaultSort={{
                field: 'device.displayName',
                direction: 'ascend',
              }}
              staticFilters={[
                { field: 'building', values: [ticket.building.id] },
                { field: 'keyType', values: ['NinjaOne'] },
              ]}
            >
              <DevicesContainer>
                <IBCard isCovered title={'Devices'}>
                  <NinjaOneDeviceTable keyType={'NinjaOne'} />
                </IBCard>
              </DevicesContainer>
            </InputContainer>
          </Modal>
        </>
      );
    }

    if (task.labels.includes('RemoveDeviceFromSentinelOne')) {
      return (
        <>
          <Button
            shape="square"
            onClick={() => setShowModal(true)}
            disabled={showModal}
            icon={<EyeOutlined />}
          />
          <Modal
            open={showModal}
            width={'50%'}
            title={`SentinelOne Devices: ${ticket.building.name}`}
            onCancel={() => {
              setShowModal(false);
            }}
            footer={[
              <Button
                key="cancel"
                onClick={() => {
                  setShowModal(false);
                }}
              >
                Cancel
              </Button>,
            ]}
          >
            <InputContainer
              filterConfig={devicesContainerConfiguration}
              defaultSort={{
                field: 'device.displayName',
                direction: 'ascend',
              }}
              staticFilters={[
                { field: 'building', values: [ticket.building.id] },
                { field: 'keyType', values: ['SentinelOne'] },
              ]}
            >
              <DevicesContainer>
                <IBCard isCovered title={'Devices'}>
                  <NinjaOneDeviceTable keyType={'SentinelOne'} />
                </IBCard>
              </DevicesContainer>
            </InputContainer>
          </Modal>
        </>
      );
    }

    if (task.labels.includes('RemoveNozomiVantageSite')) {
      return (
        <>
          <Button
            shape="square"
            onClick={() => setShowModal(true)}
            disabled={showModal}
            icon={<EyeOutlined />}
          />
          <Modal
            open={showModal}
            width={'50%'}
            title={`Remove Nozomi Building Mappings: ${ticket.building.name}`}
            onCancel={() => {
              setShowModal(false);
            }}
            footer={[
              <Button
                key="cancel"
                onClick={() => {
                  setShowModal(false);
                }}
              >
                Cancel
              </Button>,
            ]}
          >
            <BuildingMappingContainer>
              <NozomiBuildingMappingTable buildingId={ticket.building.id} />
            </BuildingMappingContainer>
          </Modal>
        </>
      );
    }

    if (task.labels.includes('RemoveNinjaOneLocation')) {
      // TODO: Implement
      return null;
    }

    if (task.labels.includes('RemoveNinjaOne')) {
      return (
        <>
          <Button
            shape="square"
            onClick={() => setShowModal(true)}
            disabled={showModal}
            icon={<EyeOutlined />}
          />
          <Modal
            open={showModal}
            width={'50%'}
            title={`Remove NinjaOne Building Mappings: ${ticket.building.name}`}
            onCancel={() => {
              setShowModal(false);
            }}
            footer={[
              <Button
                key="cancel"
                onClick={() => {
                  setShowModal(false);
                }}
              >
                Cancel
              </Button>,
            ]}
          >
            <NinjaOneMappingContainer>
              <NinjaOneMappingTable />
            </NinjaOneMappingContainer>
          </Modal>
        </>
      );
    }

    if (task.labels.includes('RemoveAuvik')) {
      return (
        <>
          <Button
            shape="square"
            onClick={() => setShowModal(true)}
            disabled={showModal}
            icon={<EyeOutlined />}
          />
          <Modal
            open={showModal}
            width={'50%'}
            title={`Remove Auvik Building Mappings: ${ticket.building.name}`}
            onCancel={() => {
              setShowModal(false);
            }}
            footer={[
              <Button
                key="cancel"
                onClick={() => {
                  setShowModal(false);
                }}
              >
                Cancel
              </Button>,
            ]}
          >
            <AuvikMappingContainer>
              <AuvikMappingTable />
            </AuvikMappingContainer>
          </Modal>
        </>
      );
    }

    if (
      task.labels.includes('MoveBuilding') &&
      permissions.internalSystemAdministrator.hasPermissions(userProfile)
    ) {
      return (
        <>
          <Button
            shape="square"
            onClick={() => setShowModal(true)}
            disabled={showModal}
            icon={<EyeOutlined />}
          />
          <BuildingMoveDialog
            currentBuilding={{
              id: ticket.building.id,
              organizationId: ticket.organization.id,
              regionId: ticket.region.id,
              name: ticket.building.name,
              addressOne: '',
              addressTwo: '',
              county: '',
              city: '',
              state: '',
              zipCode: '',
              country: '',
              location: null,
              pointsOfContact: [],
              createdBy: '',
              createdAt: new Date(),
              updatedBy: '',
              updatedAt: new Date(),
              isDeleted: false,
              type: '',
              squareFootage: 0,
              keys: [],
            }}
            open={showModal}
            onClose={() => {
              setShowModal(false);
            }}
          />
        </>
      );
    }
  }

  return null;
};

export default TaskSpecificButton;
